import { sanitizeString } from './safe-log-sanitizer';

export interface SerializedClientError {
  message: string;
  errorName?: string;
  stack?: string;
}

export const unwrapClientError = (error: unknown): unknown => {
  const seen = new Set<unknown>();
  let current = error;

  while (current && typeof current === 'object' && !seen.has(current)) {
    seen.add(current);
    let candidate: {
      ngOriginalError?: unknown;
      originalError?: unknown;
      rejection?: unknown;
      reason?: unknown;
      error?: unknown;
    };

    try {
      candidate = current as typeof candidate;
    } catch {
      break;
    }

    try {
      if (candidate.ngOriginalError) {
        current = candidate.ngOriginalError;
        continue;
      }
      if (candidate.originalError) {
        current = candidate.originalError;
        continue;
      }
      if (candidate.rejection) {
        current = candidate.rejection;
        continue;
      }
      if (candidate.reason) {
        current = candidate.reason;
        continue;
      }
      if (isErrorEvent(current) && candidate.error) {
        current = candidate.error;
        continue;
      }
    } catch {
      break;
    }
    break;
  }

  return current;
};

export const serializeClientError = (
  error: unknown,
  fallbackMessage = 'Client error',
): SerializedClientError => {
  try {
    return serializeClientErrorUnsafe(error, fallbackMessage);
  } catch {
    return { message: sanitizeString(fallbackMessage, 1000) };
  }
};

const serializeClientErrorUnsafe = (
  error: unknown,
  fallbackMessage: string,
): SerializedClientError => {
  const unwrapped = unwrapClientError(error);

  if (unwrapped instanceof Error) {
    return {
      message: sanitizeString(unwrapped.message || fallbackMessage, 1000),
      errorName: sanitizeString(unwrapped.name || 'Error', 200),
      stack: unwrapped.stack ? sanitizeString(unwrapped.stack, 10000) : undefined,
    };
  }

  if (typeof unwrapped === 'string') {
    return { message: sanitizeString(unwrapped || fallbackMessage, 1000) };
  }

  if (isErrorEvent(unwrapped)) {
    return {
      message: sanitizeString(unwrapped.message || fallbackMessage, 1000),
      errorName: 'ErrorEvent',
    };
  }

  if (unwrapped && typeof unwrapped === 'object') {
    const maybeMessage = (unwrapped as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') {
      return { message: sanitizeString(maybeMessage, 1000) };
    }
  }

  return { message: sanitizeString(fallbackMessage, 1000) };
};

export const getTopStackFrame = (stack: string | undefined): string => {
  if (!stack) return '';
  const [, firstFrame] = stack.split('\n');
  return firstFrame?.trim() ?? '';
};

const isErrorEvent = (value: unknown): value is ErrorEvent => (
  typeof ErrorEvent !== 'undefined' && value instanceof ErrorEvent
);
